import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { track } from "@/lib/track";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import DatePickerAU from "@/components/DatePickerAU";
import TownCoverageMap from "@/components/TownCoverageMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, ArrowLeft, Flower2, CreditCard, History, MessageCircleQuestion } from "lucide-react";
import { toast } from "sonner";

export default function FloristBooking() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser } = useAuth();
  useEffect(() => { refreshUser(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { track("booking_opened", { kind: "florist" }); }, []);
  const onAccount = user?.customer_type === "on_account";
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [recipientName, setRecipientName] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [stripeMode, setStripeMode] = useState("test");
  useEffect(() => { api.get("/payments/config").then((r) => setStripeMode(r.data.mode || "test")).catch(() => {}); }, []);
  const [cardMessage, setCardMessage] = useState("");
  const [flowers, setFlowers] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [townPrices, setTownPrices] = useState(null);
  const [myRequests, setMyRequests] = useState([]);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => { api.get("/florist/town-prices").then((r) => setTownPrices(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/florist/price-requests/mine").then((r) => setMyRequests(r.data)).catch(() => {}); }, []);

  const normTown = (c) => (c || "").trim().toLowerCase().replace(/\./g, "");
  const pendingForCity = dropoff?.city && myRequests.some((r) => r.status === "pending" && r.town === normTown(dropoff.city));
  const requestPrice = async () => {
    setRequesting(true);
    try {
      const { data } = await api.post("/florist/price-requests", { city: dropoff.city, lat: dropoff.lat, lng: dropoff.lng });
      setMyRequests((p) => [data, ...p]);
      toast.success(`Price request for ${dropoff.city} sent to the admin`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't send the request");
    } finally { setRequesting(false); }
  };

  useEffect(() => {
    const rs = location.state?.resend;
    if (!rs) return;
    if (rs.dropoff) setDropoff({ ...rs.dropoff, label: rs.dropoff.label || [rs.dropoff.business, rs.dropoff.line1, rs.dropoff.city, rs.dropoff.postcode].filter(Boolean).join(", ") });
    setRecipientName(rs.recipient_name || "");
    setRecipientPhone(rs.recipient_phone || "");
    setCardMessage(rs.card_message || "");
    setFlowers(rs.flowers || "");
    toast.success("Details copied from your previous delivery — pick a date and book");
  }, [location.state]);

  useEffect(() => {
    const dummy = { line1: "x", city: "x", postcode: "x" };
    api.post("/quote", { pickup: pickup || dummy, dropoff: dropoff || dummy, size: "flower_arrangement", service: "standard" })
      .then((r) => setQuote(r.data)).catch(() => {});
  }, [pickup, dropoff]);

  const book = async () => {
    if (!pickup || !dropoff) { toast.error("Select the florist and delivery addresses"); return; }
    if (!recipientName) { toast.error("Enter the recipient's name"); return; }
    setSubmitting(true);
    try {
      const orderRes = await api.post("/orders", {
        pickup: { ...pickup, contact_name: user?.name || "Florist", contact_phone: user?.phone || "" },
        dropoff: { ...dropoff, contact_name: recipientName, contact_phone: recipientPhone },
        size: "flower_arrangement", service: "standard",
        parcel_description: flowers || "Flower delivery",
        recipient_name: recipientName, card_message: cardMessage, scheduled_date: deliveryDate,
      });
      if (orderRes.data.billing !== "account") {
        const { data } = await api.post("/payments/checkout", {
          order_id: orderRes.data.id, origin_url: window.location.origin,
        });
        if (data.paid_by_credit) {
          toast.success("Paid in full with your referral credit — no card needed!");
          navigate(`/orders/${orderRes.data.id}`);
          return;
        }
        window.location.href = data.checkout_url;
        return;
      } else {
        toast.success("Your order has been queued for pickup and has been allocated to your account");
      }
      navigate(`/orders/${orderRes.data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Booking failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="florist-back-button" className="mb-4 gap-1.5">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Flower2 className="h-7 w-7 text-pink-500" />
            <h1 className="font-heading text-3xl font-extrabold tracking-tight text-kilted-green">Flower delivery</h1>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate("/flower-history")} data-testid="florist-history-button" className="gap-1.5 border-pink-300 text-pink-700 hover:bg-pink-50">
            <History className="h-4 w-4" /> Delivery history
          </Button>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Fixed-price same-region flower delivery for your shop.</p>

        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <Card>
              <CardHeader><CardTitle className="text-lg">Collect from (your shop)</CardTitle></CardHeader>
              <CardContent><AddressAutocomplete label="Florist / pickup address" onSelect={setPickup} testid="florist-pickup" /></CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-lg">Deliver to</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <AddressAutocomplete label="Recipient address" value={dropoff} onSelect={setDropoff} testid="florist-dropoff" />
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Recipient name</Label><Input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} data-testid="florist-recipient-name" /></div>
                  <div className="space-y-1.5"><Label>Recipient phone</Label><Input value={recipientPhone} onChange={(e) => setRecipientPhone(e.target.value)} data-testid="florist-recipient-phone" /></div>
                </div>
                <div className="space-y-1.5"><Label>Delivery date</Label><DatePickerAU value={deliveryDate} onChange={setDeliveryDate} minToday placeholder="Pick a date" testid="florist-date" /></div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-lg">Flowers & card</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5"><Label>Bouquet / arrangement</Label><Input value={flowers} onChange={(e) => setFlowers(e.target.value)} placeholder="e.g. Seasonal mixed bouquet" data-testid="florist-flowers" /></div>
                <div className="space-y-1.5"><Label>Card message</Label><Textarea value={cardMessage} onChange={(e) => setCardMessage(e.target.value)} placeholder="Message to include with the flowers" data-testid="florist-card-message" /></div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-1">
            <Card className="sticky top-20 border-pink-200">
              <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Flower2 className="h-5 w-5 text-pink-500" /> Fixed price</CardTitle></CardHeader>
              <CardContent>
                <div className="mb-3 flex items-center justify-between rounded-md border border-pink-200 bg-pink-50 px-3 py-2 text-sm">
                  <span className="font-medium text-pink-700">Item category</span>
                  <span className="font-semibold text-pink-800" data-testid="florist-item-category">Flower arrangement</span>
                </div>
                <div className="flex items-center justify-between rounded-md bg-kilted-green px-3 py-3 text-white">
                  <span className="font-semibold">Delivery (inc GST)</span>
                  <span className="font-heading text-2xl font-extrabold" data-testid="florist-price">${quote ? quote.total.toFixed(2) : "—"}</span>
                </div>
                <p className="mt-2 text-center text-xs text-muted-foreground" data-testid="florist-town-note">
                  {dropoff ? (quote?.town ? `Fixed rate for ${quote.town}` : "Fixed rate — other areas") : "Pick a delivery address to see your town's rate"}
                </p>
                {!onAccount && quote && (
                  <p className="mt-1 text-center text-[11px] text-muted-foreground" data-testid="florist-no-surcharge-note">No card surcharge — you pay exactly this amount at checkout.</p>
                )}
                {dropoff && quote && !quote.town && (
                  pendingForCity ? (
                    <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="price-request-pending">
                      Price request for <b>{dropoff.city}</b> sent — the admin will review it and you'll get an email when it's added.
                    </p>
                  ) : (
                    <Button variant="outline" size="sm" onClick={requestPrice} disabled={requesting} data-testid="request-price-button"
                      className="mt-3 w-full gap-1.5 border-kilted-gold text-kilted-green hover:bg-kilted-gold/10">
                      {requesting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageCircleQuestion className="h-3.5 w-3.5" />}
                      Ask admin for a {dropoff.city} rate
                    </Button>
                  )
                )}
                <Button onClick={book} disabled={submitting || !pickup || !dropoff} data-testid="florist-book-button" className="mt-4 w-full gap-2 bg-pink-500 text-white hover:bg-pink-600">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} {onAccount ? "Confirm delivery" : "Book & pay"}
                </Button>
                <p className="mt-2 text-center text-[11px] text-muted-foreground">{onAccount ? "Billed to your monthly account" : `Flat rate · secured by Stripe${stripeMode === "live" ? "" : " (test mode)"}`}</p>
              </CardContent>
            </Card>
            <Card className="mt-4" data-testid="town-coverage-card">
              <CardHeader><CardTitle className="text-base">Coverage & rates</CardTitle></CardHeader>
              <CardContent>
                <TownCoverageMap towns={townPrices?.towns} coords={townPrices?.coords} />
                <p className="mt-2 text-xs text-muted-foreground">Tap a pin for the town's fixed rate. Anywhere else: <span className="font-semibold">${Number(townPrices?.default_price ?? 25).toFixed(2)}</span> inc GST.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
